import { supabase } from '../client';
import { isPostgrestRelationMissing } from '../postgrestErrors';

export type ScorecardRecommendation = 'strong_yes' | 'yes' | 'maybe' | 'no' | 'strong_no';

export interface RubricItem {
  criterion: string;
  score: number | null;
  note: string;
}

export interface InterviewScorecard {
  id: string;
  org_id: string;
  application_id: string;
  interview_event_id: string | null;
  interviewer_id: string | null;
  interviewer_name: string | null;
  round: string;
  overall_score: number | null;
  recommendation: ScorecardRecommendation | null;
  rubric: RubricItem[];
  notes: string | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

export type ScorecardCreate = Pick<
  InterviewScorecard,
  'application_id' | 'round' | 'overall_score' | 'recommendation' | 'rubric' | 'notes'
> & {
  org_id: string;
  interviewer_id?: string | null;
  interviewer_name?: string | null;
  interview_event_id?: string | null;
};

export const RECOMMENDATION_LABELS: Record<ScorecardRecommendation, string> = {
  strong_yes: 'Strong Yes',
  yes:        'Yes',
  maybe:      'Maybe',
  no:         'No',
  strong_no:  'Strong No',
};

export const RECOMMENDATION_COLORS: Record<ScorecardRecommendation, string> = {
  strong_yes: 'text-emerald-400',
  yes:        'text-green-400',
  maybe:      'text-yellow-400',
  no:         'text-orange-400',
  strong_no:  'text-red-400',
};

export const DEFAULT_RUBRIC: RubricItem[] = [
  { criterion: 'Technical / domain knowledge', score: null, note: '' },
  { criterion: 'Problem solving', score: null, note: '' },
  { criterion: 'Communication', score: null, note: '' },
  { criterion: 'Culture fit', score: null, note: '' },
  { criterion: 'Motivation & interest', score: null, note: '' },
];

export const scorecardService = {
  async listByApplication(applicationId: string): Promise<InterviewScorecard[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_scorecards')
      .select('*')
      .eq('application_id', applicationId)
      .order('submitted_at', { ascending: false });
    if (error) {
      if (isPostgrestRelationMissing(error)) return [];
      throw error;
    }
    return (data ?? []) as InterviewScorecard[];
  },

  async create(payload: ScorecardCreate): Promise<InterviewScorecard> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_scorecards')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data as InterviewScorecard;
  },

  async update(id: string, payload: Partial<Pick<InterviewScorecard, 'overall_score' | 'recommendation' | 'rubric' | 'notes'>>): Promise<InterviewScorecard> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_scorecards')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as InterviewScorecard;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_interview_scorecards')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};
